// Editable payload for Oregon Form OR-AUTH-REP (Authorization to Represent).
// snake_case keys mirror `apps/forms/mappings/form_orauthrep.py` `build_defaults`
// exactly (the submission `data` blob is passed through verbatim, no snake→camel
// mapping). OR-AUTH-REP is the power-of-attorney sibling of OR-AUTH-INFO: it
// prints a single representative + the same tax-year/program limitation rows,
// and additionally grants representation authority — so it carries the
// representation-scope acts block (grant + taxpayer authorizing initials) that
// the disclosure-only OR-AUTH-INFO does not.

import type { PractitionerDesignation } from "@/features/forms/types";

/** A practitioner assigned to the client, offered as the single rep choice. */
export interface FormOrauthrepRepresentative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
}

/** One authorized-matter row: a tax year/period + a tax program. */
export interface FormOrauthrepMatter {
  tax_years: string;
  tax_programs: string;
}

export interface FormOrauthrepData {
  generate_for: "taxpayer" | "spouse";
  /** The single selected representative's practitioner id ("choose one per form"). */
  active_rep: string;
  representatives: FormOrauthrepRepresentative[];
  matters: FormOrauthrepMatter[];
  /** Whether the taxpayer's tax ID is an SSN or ITIN (only the last four print). */
  tin_type: "SSN" | "ITIN";
  /** Business Identification Number (business clients only). */
  bin: string;
  /**
   * Whether this authorization grants representation (not just disclosure). When
   * true, the taxpayer's authorizing initials are printed on the form.
   */
  grant_representation: boolean;
  /** The taxpayer's initials attesting the grant of representation authority. */
  taxpayer_initials: string;
}

/**
 * How many matter rows fit on the form before spilling to a continuation sheet
 * (mirrors `_MAX_MATTERS` in the backend mapper).
 */
export const MAX_ORAUTHREP_MATTERS = 4;
